import type { RoomId } from "./rooms";
import type { WalletAddress } from "./wallet";

/**
 * Lobby rules (owner decision, revision 3): registering means you're in, there is no ready check.
 * Once minPlayers have registered a wait of countdownMs starts; registration stays open during it,
 * and the round starts when it runs out, or immediately (fullLobbyCountdownMs) when the room is full.
 */
export const ROUND_LIMITS = Object.freeze({
	minPlayers: 10,
	maxPlayers: 25,
	/** unused since revision 3 (no ready check); kept for restored legacy rounds */
	readyCheckMs: 0,
	countdownMs: 80_000,
	fullLobbyCountdownMs: 0,
	/** disconnected players remain stationary and vulnerable for this long before elimination */
	reconnectGraceMs: 30_000,
	spectatorDelayMs: 15_000,
	provisionalReviewMs: 10 * 60_000,
});

/*
 * Public rounds follow their room's limits (ROUND_LIMITS but for the Token room's 30 seats: TOKEN_ROUND_LIMITS in
 * rooms.ts) in every mode, development included. In development the Quick start button (POST /api/dev/quick-start)
 * still starts a round at once with whoever has registered, and bots fill the empty seats of that round only (a
 * practice round, no stats).
 */

/**
 * the most players any room's round holds: the Token room's (owner decision, 19 October 2026: "Make the token fees
 * room 30 players"); every other room's rounds hold ROUND_LIMITS.maxPlayers (25). Placements and scores go up to it
 */
export const MAX_ROUND_PLAYERS = 30;

export interface RoundLimits {
	readonly minPlayers: number;
	readonly maxPlayers: number;
	readonly readyCheckMs: number;
	readonly countdownMs: number;
	readonly fullLobbyCountdownMs: number;
	readonly reconnectGraceMs: number;
	readonly spectatorDelayMs: number;
	readonly provisionalReviewMs: number;
}

export enum RoundState {
	Waiting = "waiting",
	ReadyCheck = "ready_check",
	Countdown = "countdown",
	InProgress = "in_progress",
	Provisional = "provisional",
	Finalized = "finalized",
	Cancelled = "cancelled",
}

export enum RegistrationState {
	Registered = "registered",
	Ready = "ready",
	Admitted = "admitted",
	Playing = "playing",
	Eliminated = "eliminated",
	Disqualified = "disqualified",
	Removed = "removed",
}

export interface RoundRegistration {
	readonly roundId: string;
	readonly address: WalletAddress;
	readonly registeredAt: number;
	readonly state: RegistrationState;
	readonly readyAt?: number;
	/** Immutable ownership snapshot taken when this queue seat is created. */
	readonly walletVerifiedAtAdmission?: boolean;
	/** Immutable reward eligibility snapshot; legacy/restored rows default to false. */
	readonly rewardEligibleAtAdmission?: boolean;
}

export interface RoundParticipant {
	readonly roundId: string;
	readonly address: WalletAddress;
	readonly registrationOrder: number;
	readonly placement: number;
	readonly kills: number;
	readonly damageDone: number;
	readonly score: number;
	readonly eligible: boolean;
	/** Reward eligibility is distinct from combat/stat eligibility and is never client-authored. */
	readonly rewardEligible?: boolean;
	readonly disconnectedAt?: number;
	/**
	 * admitted to the round, never in its match (listed last, ineligible): set by the round's coordinator, never by a
	 * game. The stats pages don't count it as a round played (its entry still counts as spent)
	 */
	readonly absent?: boolean;
	/** disqualified in the round's review (ineligible for that reason, not merely gone): set by the coordinator only */
	readonly disqualified?: boolean;
}

export interface RoundSummary {
	readonly id: string;
	readonly roomId: RoomId;
	readonly roomConfigVersion: number;
	readonly state: RoundState;
	readonly registrationOpenedAt: number;
	readonly readyDeadline?: number;
	readonly startsAt?: number;
	readonly startedAt?: number;
	readonly endedAt?: number;
	readonly finalizesAt?: number;
	readonly registeredCount: number;
	readonly readyCount: number;
	readonly playingCount: number;
	readonly spectatorCount: number;
	/** development: bots filled the round, so it doesn't count for stats, leaderboards or rewards */
	readonly practice?: boolean;
}

/** points for each place, 1st to 30th (MAX_ROUND_PLAYERS): 1 from the 19th place down, the 26th to 30th (0.83) too */
export const PLACEMENT_POINTS = Object.freeze([
	0, 100, 70, 50, 35, 25, 20, 17, 15, 13, 11, 9, 7, 6, 5, 4, 3, 2, 1, 1, 1, 1,
	1, 1, 1, 1, 1, 1, 1, 1, 1,
]);

export function calculateRoundScore(placement: number, kills: number): number {
	if (
		!Number.isInteger(placement) ||
		placement < 1 ||
		placement > MAX_ROUND_PLAYERS
	) {
		throw new RangeError(
			`Placement must be between 1 and ${MAX_ROUND_PLAYERS}`,
		);
	}
	if (!Number.isInteger(kills) || kills < 0) {
		throw new RangeError("Kills must be a non-negative integer");
	}
	return PLACEMENT_POINTS[placement] + kills * 10;
}

export function rankRoundParticipants(
	participants: readonly RoundParticipant[],
): RoundParticipant[] {
	return participants
		.filter((participant) => participant.eligible)
		.sort(
			(a, b) =>
				b.score - a.score ||
				a.placement - b.placement ||
				b.damageDone - a.damageDone ||
				a.registrationOrder - b.registrationOrder,
		);
}
